import sample from 'utils/sample';
import { ORIENTATION, PLACEHOLDER_TYPE, TEMPLATE_LAYOUT } from './constants';
import { getPhotoPlaceholders, getTextPlaceholders } from './placeholders';
import { getPhotoOrientationType, getPlaceholderOrientationType } from './geometry';
import { getLayoutType, getOriginalPage, getTemplatePage } from './page';

export function getPageFilters(template) {
  return template.template.filters || [];
}

export function getSuitablePage(pages, photo) {
  const suitablePages = pages
    .filter((p) => {
      // Do not choose pages with text placeholder as default.
      return getPhotoPlaceholders(p).length === 1 && getTextPlaceholders(p).length === 0;
    })
    .filter((p) => {
      return (
        getPlaceholderOrientationType(getPhotoPlaceholders(p)[0]) === getPhotoOrientationType(photo)
      );
    });

  let page = sample(suitablePages);

  if (!page) {
    page = sample(pages);
  }

  return page;
}

// Walks the photo slots page by page like the generator's fixed and sequence
// layouts, which fill pages in template order.
const getPageBySlot = (pages, index, slotTypes) => {
  let offset = 0;
  return pages.find((page) => {
    offset += (page.placeholders ?? []).filter((p) => slotTypes.includes(p.type)).length;
    return index < offset;
  });
};

/**
 * The page the template generator will place a photo on, mirroring
 * `parabo-serverless/src/builders/book.js` and `layouts/*`. The generator never
 * rotates a photo, so the page frame applies as it is. Automatic capacity
 * chunking (legacy books with more photos than pages) is not mirrored and
 * falls back to the first candidate.
 *
 * @param {object} template
 * @param {{ width: number, height: number }} photo
 * @param {object} [options]
 * @param {string} [options.pageId] - Selected page (page and sequence layouts).
 * @param {number} [options.index] - Position among the interior photos.
 * @param {number} [options.photosCount] - Interior photos, for the best-fit gate.
 * @returns {object|undefined}
 */
export function getGeneratorPage(template, photo, { pageId, index = 0, photosCount = 0 } = {}) {
  const layout = getLayoutType(template);

  if (layout === TEMPLATE_LAYOUT.FIXED) {
    const slotTypes = [PLACEHOLDER_TYPE.PHOTO, PLACEHOLDER_TYPE.MEDIA];
    return getPageBySlot(template.pages, index, slotTypes) ?? template.pages[0];
  }

  if (layout === TEMPLATE_LAYOUT.SEQUENCE) {
    const start = Math.max(0, template.pages.findIndex((p) => p.id === pageId));
    const pages = template.pages.slice(start);
    return getPageBySlot(pages, index, [PLACEHOLDER_TYPE.PHOTO]) ?? pages[0];
  }

  if (layout !== TEMPLATE_LAYOUT.AUTOMATIC) {
    return getTemplatePage(template, pageId) ?? template.pages[0];
  }

  const candidates = template.pages.filter(
    (p) =>
      getTextPlaceholders(p).length === 0 && !(p.group !== undefined && p.group_default !== true),
  );

  if (candidates.every((p) => p.placeholders.length === 1)) {
    let defaults = candidates.filter((p) => p.group_default === true);
    if (defaults.length === 0) {
      defaults = candidates.filter((p) => p.group === undefined);
    }

    const landscape = defaults.find((p) => p.orientation === ORIENTATION.LANDSCAPE);
    const portrait = defaults.find((p) => p.orientation === ORIENTATION.PORTRAIT);
    if (!landscape || !portrait) {
      return defaults[0];
    }
    // Square photos have no matching page and take the landscape one.
    return getPhotoOrientationType(photo) === ORIENTATION.PORTRAIT ? portrait : landscape;
  }

  const bestFit = candidates
    .filter((p) => p.placeholders.length === 1 && p.span !== 1 && [PLACEHOLDER_TYPE.PHOTO, PLACEHOLDER_TYPE.MEDIA].includes(p.placeholders[0].type))
    .map((page) => {
      const [, , width, height] = page.placeholders[0].displayFrame ?? page.placeholders[0].frame ?? [];
      return width > 0 && height > 0 ? { page, logRatio: Math.log(width / height) } : null;
    })
    .filter(Boolean);

  const { minimumNumberOfPages = 0, maximumNumberOfPages, maximumNumberOfPhotos, numberOfPhotos } =
    template.template;
  const upperPhotoCount = maximumNumberOfPhotos ?? numberOfPhotos ?? photosCount;
  const canUseBestFit =
    bestFit.length > 0 &&
    (maximumNumberOfPages === undefined ||
      Math.max(minimumNumberOfPages, upperPhotoCount) <= maximumNumberOfPages);
  if (!canUseBestFit) {
    return candidates[0];
  }

  const photoLogRatio = photo.width > 0 && photo.height > 0 ? Math.log(photo.width / photo.height) : 0;
  // Ties keep the earlier template page, like the generator.
  return bestFit.reduce((best, candidate) =>
    Math.abs(candidate.logRatio - photoLogRatio) < Math.abs(best.logRatio - photoLogRatio)
      ? candidate
      : best,
  ).page;
}

export function getSampleBookPage(template, type = 'pages', forbiddenPageIds = []) {
  const pages = template[type].filter((p) => forbiddenPageIds.indexOf(p.id) < 0);

  if (pages.length > 0) {
    const suitablePages = pages.filter((p) => {
      // Do not choose pages with text placeholder as default.
      return getPhotoPlaceholders(p).length === 1 && getTextPlaceholders(p).length === 0;
    });

    if (suitablePages.length > 0) {
      return sample(suitablePages);
    }
    return sample(pages);
  }

  return sample(template[type]);
}

export function getPossiblePages(template) {
  if (getLayoutType(template) === TEMPLATE_LAYOUT.AUTOMATIC) {
    return template.pages;
  }
  return [];
}

export function getPageGroup(template, page) {
  const originalPage = getOriginalPage(template, page);

  return {
    name: originalPage.group || '',
    enabled: !!originalPage.group_default,
  };
}

export function getOtherPageOfGroup(template, page) {
  const pageGroup = getPageGroup(template, page);

  return template.pages.find((p) => p.group === pageGroup.name && p.id !== page.id);
}

export function getPossiblePagesVariants(template, pages, renderCallback) {
  const variants = [];

  const pageGroup = getPageGroup(template, pages[0]);

  if (pageGroup.name.indexOf('borders') >= 0) {
    variants.push({
      type: 'BORDER',
      checked: pageGroup.enabled,
      title: 'INCLUDE WHITE BORDER',
      description: 'Toggle switch to add/remove the white border on your prints.',
    });
  } else if (pageGroup.name.indexOf('pexeso') >= 0) {
    variants.push({
      type: 'PEXESO',
      checked: pageGroup.enabled,
      title: 'INCLUDE DATE',
      description: 'Toggle switch to add/remove the date for each person.',
    });
  }

  return variants.map(renderCallback);
}

export function getPagesFilterVariants(template, pages, renderCallback) {
  const filters = [];

  getPageFilters(template).forEach((filter) => {
    filters.push({
      type: filter,
      checked: pages[0].filter === filter,
      title: 'Prefer Black & White?',
      description: 'Hit the switch to convert your print to B&W.',
    });
  });

  return filters.map(renderCallback);
}
